import { Component, OnInit, Input } from '@angular/core';
import { AbstractControl, ControlContainer, FormGroup, FormGroupDirective, ValidatorFn } from '@angular/forms';
import { GetOpenEnrollmentRule } from '../../../../family-form/types/qualifying-life-event.response.interface';
import { DateOfLifeEventCustomAttributes } from '../../interfaces/special-enrollment-qualification-page-proxy.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { DateService } from 'src/app/shared/services/date.service';
import { NgRedux } from '@ptl/angular-redux-store';
import { StateStructure } from '@ptl/state';
import { setLifeEventDate } from 'src/app/shared/reducers/sep-life-event-form.reducer';

interface ComponentFormControls {
  dateOfLifeEvent: AbstractControl;
}

@Component({
  selector: 'app-date-of-life-event-single-page-proxy-form',
  templateUrl: './date-of-life-event-single-page-proxy-form.component.html',
  styleUrls: ['./date-of-life-event-single-page-proxy-form.component.scss'],
  standalone: false,
  //this allows us detect the form being used as a wrapper for this component in the parent component 
  viewProviders: [ 
    { 
      provide: ControlContainer,
      useExisting: FormGroupDirective
    } 
  ]
})
export class DateOfLifeEventSinglePageProxyFormComponent implements OnInit {

  @Input() componentDisplayAttributes: DateOfLifeEventCustomAttributes;

  dateForm: FormGroup;
  lifeEvent: GetOpenEnrollmentRule;
  proxyLifeEventConnectPath: string;

  firstOfMonthErrorMessage: string;
  outOfRangeErrorMessage: string;
  requiredErrorMessage: string;

  fetchFromConfig: boolean;

  constructor(
    private formGroupDirective: FormGroupDirective,
    private reducerUtilityService: ReducerUtilityService,
    private dateService: DateService,
    private ngRedux: NgRedux<StateStructure>
  ) {}

  async ngOnInit(): Promise<void> {;
    this.fetchFromConfig = this.reducerUtilityService.getComponentStates().SepLifeEventForm.fetchFromConfig
    //connect the parent form
    this.dateForm = this.formGroupDirective.form;

    //necessary to add within this file to maintain access to the validate function
    this.dateForm.get(this.componentDisplayAttributes.formControlAttributes.formControlName).addValidators(this.validate())
    this.dateForm.get(this.componentDisplayAttributes.formControlAttributes.formControlName).updateValueAndValidity();
    this.setDynamicAttributes();
  }

  get dateFormControls(): ComponentFormControls {
    return this.dateForm?.controls as unknown as ComponentFormControls;
  }

  dateChanged(type: any, event: any) {
    this.dateFormControls[this.componentDisplayAttributes.formControlAttributes.formControlName].updateValueAndValidity();
  }

  onDateSelected(dateValue: string): void {
    // Store in Redux
    this.ngRedux.dispatch(setLifeEventDate(dateValue));
  }

  setDynamicAttributes() {
    const payloadStates = this.reducerUtilityService.getPayloadStates();
    this.lifeEvent = this.fetchFromConfig ? payloadStates?.['special-enrollment-circumstance']?.[this.proxyLifeEventConnectPath] as GetOpenEnrollmentRule
    : this.reducerUtilityService.getComponentStates().SepLifeEventForm?.selectedLifeEvent;
    this.outOfRangeErrorMessage = this.componentDisplayAttributes?.custom?.dateOfLifeEventRangeErrorMessage;
    this.firstOfMonthErrorMessage = this.componentDisplayAttributes?.custom?.firstOfMonthErrorMessage;
    this.requiredErrorMessage = this.componentDisplayAttributes?.custom?.dateOfLifeEventRequiredErrorMessage;
  }

  validate(): ValidatorFn {
    return (control: AbstractControl) => {
      const payloadStates = this.reducerUtilityService.getPayloadStates();
      const lifeEventValue = this.fetchFromConfig ? payloadStates?.['special-enrollment-circumstance']?.[this.componentDisplayAttributes.proxyLifeEventConnectPath]
      : this.reducerUtilityService.getComponentStates().SepLifeEventForm?.selectedLifeEvent;
      const lifeEventDateControl = this.dateFormControls[this.componentDisplayAttributes.formControlAttributes.formControlName];

      // Control not yet ready for validation
      if (!lifeEventDateControl) { 
        throw new Error("dateOfLifeEvent control does not exist!")
      }


      const lifeEventDateValue: moment.Moment = lifeEventDateControl.value ? this.dateService.getDateAsMoment(lifeEventDateControl.value) : null;
      
      let isValid = true;
      const errors = {
        rangeError: false,
        firstOfMonthError: false
      }

      /** Range */
      if (lifeEventValue && lifeEventDateValue) {

        errors.rangeError = !this.isValidEnrollmentDate(
          lifeEventDateValue,
          lifeEventValue.startDateRuleDays,
          lifeEventValue.endDateRuleDays
        );
        if (errors.rangeError) {
          isValid = false;
        }

        const isEventOnFirstOfMonth = lifeEventDateValue.date() === 1;
        /** Custom */
        if (this.componentDisplayAttributes.custom?.dateMustBeFirstOfMonth && !isEventOnFirstOfMonth) {
          errors.firstOfMonthError = true;
          isValid = false;
        }

      } else {
        isValid = false;
      }

      if (isValid) {
        return null;
      } else {
        if (errors.firstOfMonthError) {
          return { firstOfMonth: true };
        }
        if (errors.rangeError) {
          return { outOfRange: true };
        }
      }
    }
  }

  /**
   * 
   * @param dateToCheck - life event date
   * @param endOfDateWindow # of days BEFORE current date that event-date is valid
   * @param beginningOfDateWindow - # of days AFTER current date that event-date still valid
   * @returns 
   */
  isValidEnrollmentDate(dateToCheck: moment.Moment, endOfDateWindow: string, beginningOfDateWindow: string) {

    if (
      !(endOfDateWindow?.length > 0) &&
      !(beginningOfDateWindow?.length > 0)
    ) {
      return false;
    } else {

      const lastValidDateInWindow: moment.Moment = this.dateService.getTodayAsMoment();
      const firstValidDateInWindow: moment.Moment = this.dateService.getTodayAsMoment();

      if (endOfDateWindow && beginningOfDateWindow) {
        lastValidDateInWindow.date(lastValidDateInWindow.date() + parseInt(endOfDateWindow, 10));
        firstValidDateInWindow.date(firstValidDateInWindow.date() - parseInt(beginningOfDateWindow, 10) - 1);

        return (lastValidDateInWindow >= dateToCheck && firstValidDateInWindow <= dateToCheck) ? true : false;

      } else if (beginningOfDateWindow) {

        firstValidDateInWindow.date(firstValidDateInWindow.date() - parseInt(beginningOfDateWindow, 10) - 1);

        return (lastValidDateInWindow >= dateToCheck && firstValidDateInWindow <= dateToCheck) ? true : false;

      } else {
        console.error('something wrong in the date');
        return false;
      }

    }
  }

}
